import { error } from '@innovyze/stylovyze';
import {
	inferByIndustryStandardCurve,
	getCurveConfig as getCurveConfigService,
	inferFailureDefinition,
} from '@Services/deteriorationModels.services';
import { useGlobalization } from '@Translations/useGlobalization';
import {
	CurveMethod,
	WeibullDeclineReason,
	WeibullFormulation,
	mapCurveMethodResponseToCurveMethod,
} from '@Types/deteriorationModels.types';
import { AxiosResponse } from 'axios';
import React, { useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { CurvePoint, isFlatCurve } from '../utils';
import { mapFailureDefinitionResponse } from '../hooks';

interface BulkCurveState {
	selectedCurveMethod: CurveMethod;
	customCurvePoints: CurvePoint[];
	industryStandardCurve: string | null;
	industryStandardCurvePoints: CurvePoint[];
	industryStandardCurveLoading: boolean;
	industryStandardCurveAllData: Record<string, CurvePoint[]>;
	oneExpectedUsefulLife: number | null;
	copyCurve: string | null;
	copyCurveName: null;
	copiedCurvePoints: CurvePoint[];
	copiedCurveLoading: boolean;
	// Per-formulation fits from a weibull copy source. Empty for custom/industry sources, whose
	// single curve lives in copiedCurvePoints.
	copiedCurveAllData: Partial<Record<WeibullFormulation, CurvePoint[]>>;
	// Why a formulation is missing from `copiedCurveAllData`, keyed the same way. Empty for a
	// source group trained before I360A-2882, which the banner treats as "reason unspecified".
	copiedCurveDeclines: Partial<Record<WeibullFormulation, WeibullDeclineReason>>;
	weibullFormulation: WeibullFormulation;
}

export const initialBulkState: BulkCurveState = {
	selectedCurveMethod: CurveMethod.UNDEFINED,
	customCurvePoints: [],
	industryStandardCurve: null,
	industryStandardCurvePoints: [],
	industryStandardCurveLoading: false,
	industryStandardCurveAllData: {},
	oneExpectedUsefulLife: null,
	copyCurve: null,
	copyCurveName: null,
	copiedCurvePoints: [],
	copiedCurveLoading: false,
	copiedCurveAllData: {},
	copiedCurveDeclines: {},
	weibullFormulation: WeibullFormulation.TWO_PARAMETER,
};

type BulkCurveAction =
	| { type: 'SET_SELECTED_METHOD'; payload: CurveMethod }
	| { type: 'SET_CUSTOM_CURVE_POINTS'; payload: CurvePoint[] }
	| { type: 'SET_INDUSTRY_CURVE'; payload: string | null }
	| { type: 'SET_INDUSTRY_CURVE_LOADING'; payload: boolean }
	| { type: 'SET_INDUSTRY_CURVE_DATA'; payload: { allData: Record<string, CurvePoint[]> } }
	| { type: 'SET_INDUSTRY_CURVE_POINTS'; payload: CurvePoint[] }
	| { type: 'SET_ONE_EXPECTED_LIFE'; payload: number | null }
	| { type: 'SET_COPY_CURVE'; payload: string | null }
	| { type: 'SET_COPIED_CURVE_LOADING'; payload: boolean }
	| { type: 'SET_COPIED_CURVE_POINTS'; payload: CurvePoint[] }
	| {
			type: 'SET_COPIED_CURVE_ALL_DATA';
			payload: {
				allData: Partial<Record<WeibullFormulation, CurvePoint[]>>;
				declines: Partial<Record<WeibullFormulation, WeibullDeclineReason>>;
			};
	  }
	| { type: 'SET_WEIBULL_FORMULATION'; payload: WeibullFormulation };

export function bulkCurveReducer(state: BulkCurveState, action: BulkCurveAction): BulkCurveState {
	switch (action.type) {
		case 'SET_SELECTED_METHOD':
			return { ...state, selectedCurveMethod: action.payload };
		case 'SET_CUSTOM_CURVE_POINTS':
			return { ...state, customCurvePoints: action.payload };
		case 'SET_INDUSTRY_CURVE':
			return { ...state, industryStandardCurve: action.payload };
		case 'SET_INDUSTRY_CURVE_LOADING':
			return { ...state, industryStandardCurveLoading: action.payload };
		case 'SET_INDUSTRY_CURVE_DATA':
			return { ...state, industryStandardCurveAllData: action.payload.allData };
		case 'SET_INDUSTRY_CURVE_POINTS':
			return { ...state, industryStandardCurvePoints: action.payload };
		case 'SET_ONE_EXPECTED_LIFE':
			return { ...state, oneExpectedUsefulLife: action.payload };
		case 'SET_COPY_CURVE':
			// Same two rules as the per-group reducer: a same-key dispatch changes nothing (the picker
			// fires on every click and the fetch effect keys on this value, so clearing would strand the
			// preview empty), and a real change drops everything describing the outgoing source, points
			// included -- CurveFitNotes is shared with the per-group preview.
			if (action.payload === state.copyCurve) {
				return state;
			}
			return {
				...state,
				copyCurve: action.payload,
				copiedCurvePoints: [],
				copiedCurveAllData: {},
				copiedCurveDeclines: {},
			};
		case 'SET_COPIED_CURVE_LOADING':
			return { ...state, copiedCurveLoading: action.payload };
		case 'SET_COPIED_CURVE_POINTS':
			return { ...state, copiedCurvePoints: action.payload, copiedCurveAllData: {}, copiedCurveDeclines: {} };
		case 'SET_COPIED_CURVE_ALL_DATA':
			return {
				...state,
				copiedCurvePoints: [],
				copiedCurveAllData: action.payload.allData,
				copiedCurveDeclines: action.payload.declines,
			};
		case 'SET_WEIBULL_FORMULATION':
			return { ...state, weibullFormulation: action.payload };
		default:
			return state;
	}
}

export const useBulkCurveConfig = (
	modelId: string,
	industryStandardCurves: { curves: { key: string; displayName: string }[]; isLoading: boolean },
) => {
	const [state, dispatch] = useReducer(bulkCurveReducer, initialBulkState);
	const globalDispatch = useDispatch();
	const { t } = useGlobalization();
	// Sequence guard: the copy source can change mid-flight, so a slower earlier response must not
	// land last and chart the wrong group.
	const latestKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (
			state.selectedCurveMethod === CurveMethod.INDUSTRY_CURVE &&
			!industryStandardCurves.isLoading &&
			Object.keys(state.industryStandardCurveAllData).length === 0
		) {
			const fetchData = async () => {
				dispatch({ type: 'SET_INDUSTRY_CURVE_LOADING', payload: true });
				try {
					const allCurveKeys = industryStandardCurves.curves.map(curve => curve.key);
					const response = (await inferByIndustryStandardCurve({
						curveKeys: allCurveKeys,
					})) as AxiosResponse<{ data: Record<string, { age: number; survival_probability: number }[]> }>;

					const allCurveData: Record<string, CurvePoint[]> = {};
					Object.entries(response.data.data).forEach(([curveKey, curveData]) => {
						if (curveData) {
							allCurveData[curveKey] = curveData.map(point => ({
								age: point.age,
								survivalProbability: point.survival_probability * 100,
							}));
						}
					});
					dispatch({ type: 'SET_INDUSTRY_CURVE_DATA', payload: { allData: allCurveData } });
				} catch (e) {
					console.error('Failed to fetch industry standard curve points', e);
					globalDispatch(error(t('Failed to fetch industry standard curve points')));
					dispatch({ type: 'SET_INDUSTRY_CURVE_DATA', payload: { allData: {} } });
				} finally {
					dispatch({ type: 'SET_INDUSTRY_CURVE_LOADING', payload: false });
				}
			};

			fetchData();
		}
	}, [state.selectedCurveMethod, industryStandardCurves, state.industryStandardCurveAllData, globalDispatch, t]);

	useEffect(() => {
		if (state.selectedCurveMethod === CurveMethod.INDUSTRY_CURVE && state.industryStandardCurve) {
			const selectedCurveData = state.industryStandardCurveAllData[state.industryStandardCurve];
			if (selectedCurveData) {
				dispatch({ type: 'SET_INDUSTRY_CURVE_POINTS', payload: selectedCurveData });
			} else {
				dispatch({ type: 'SET_INDUSTRY_CURVE_POINTS', payload: [] });
			}
		}
	}, [state.selectedCurveMethod, state.industryStandardCurve, state.industryStandardCurveAllData]);

	useEffect(() => {
		if (state.selectedCurveMethod === CurveMethod.COPIED_CURVE && state.copyCurve) {
			latestKeyRef.current = state.copyCurve;
			const fetchData = async () => {
				dispatch({ type: 'SET_COPIED_CURVE_LOADING', payload: true });
				try {
					const sourceConfigResponse = await getCurveConfigService(modelId, state.copyCurve ?? '');
					const sourceConfig = sourceConfigResponse.data.data.selected_curve_method;
					const sourceMethod = mapCurveMethodResponseToCurveMethod(sourceConfig.method) as CurveMethod;
					let curvePoints: CurvePoint[] = [];
					const customCurveData = sourceConfig.custom_data;
					if (sourceMethod === CurveMethod.CUSTOM_CURVE) {
						if (Array.isArray(customCurveData)) {
							curvePoints = customCurveData.map(point => ({
								age: point.age,
								survivalProbability: point.survival_probability * 100,
							}));
						}
					} else if (sourceMethod === CurveMethod.INDUSTRY_CURVE && sourceConfig.industry_curve_key) {
						const industryResponse = (await inferByIndustryStandardCurve({
							curveKeys: [sourceConfig.industry_curve_key],
						})) as AxiosResponse<{ data: Record<string, { age: number; survival_probability: number }[]> }>;

						const industryData = industryResponse.data.data[sourceConfig.industry_curve_key];
						if (industryData) {
							curvePoints = industryData.map(point => ({
								age: point.age,
								survivalProbability: point.survival_probability * 100,
							}));
						}
					} else if (sourceMethod === CurveMethod.FAILURE_DEFINITION) {
						const failureResponse = await inferFailureDefinition(modelId, state.copyCurve ?? '');

						// Keep every formulation the source was fitted with; the selection is local, so
						// toggling 2P/3P does not re-issue this request. Shared with the per-group path so
						// the "a key with no points is not a fit" rule cannot drift between the two.
						const { allData, declines } = mapFailureDefinitionResponse(failureResponse.data);

						if (latestKeyRef.current !== state.copyCurve) return;
						dispatch({ type: 'SET_COPIED_CURVE_ALL_DATA', payload: { allData, declines } });
						return;
					}
					if (latestKeyRef.current !== state.copyCurve) return;
					dispatch({ type: 'SET_COPIED_CURVE_POINTS', payload: curvePoints });
				} catch (e) {
					if (latestKeyRef.current !== state.copyCurve) return;
					console.error('Failed to fetch copied curve data', e);
					globalDispatch(error(t('Failed to fetch copied curve data')));
					dispatch({ type: 'SET_COPIED_CURVE_POINTS', payload: [] });
				} finally {
					if (latestKeyRef.current === state.copyCurve) {
						dispatch({ type: 'SET_COPIED_CURVE_LOADING', payload: false });
					}
				}
			};

			fetchData();
		}
	}, [state.selectedCurveMethod, state.copyCurve, modelId]);

	// Local formulation selection, mirroring the per-group path.
	const copiedCurvePoints = useMemo(() => {
		if (Object.keys(state.copiedCurveAllData).length === 0) return state.copiedCurvePoints;
		return (
			state.copiedCurveAllData[state.weibullFormulation] ??
			state.copiedCurveAllData[WeibullFormulation.TWO_PARAMETER] ??
			[]
		);
	}, [state.copiedCurveAllData, state.copiedCurvePoints, state.weibullFormulation]);

	const selectedFormulationUnavailable = useMemo(() => {
		if (state.selectedCurveMethod !== CurveMethod.COPIED_CURVE) return false;
		if (Object.keys(state.copiedCurveAllData).length === 0) return false;
		return state.copiedCurveAllData[state.weibullFormulation] === undefined;
	}, [state.selectedCurveMethod, state.copiedCurveAllData, state.weibullFormulation]);

	// Knowable for the same reason as `selectedFormulationUnavailable`: the copy source is one
	// known group, so a diverged fit copied from it is just as detectable here as per-group.
	const selectedFitDegenerate = useMemo(() => {
		if (state.selectedCurveMethod !== CurveMethod.COPIED_CURVE) return false;
		if (Object.keys(state.copiedCurveAllData).length === 0) return false;
		return isFlatCurve(copiedCurvePoints);
	}, [state.selectedCurveMethod, state.copiedCurveAllData, copiedCurvePoints]);

	const setSelectedCurveMethod = useCallback((method: CurveMethod) => {
		dispatch({ type: 'SET_SELECTED_METHOD', payload: method });
	}, []);

	const setCustomCurvePoints = useCallback(
		(points: React.SetStateAction<CurvePoint[]>) => {
			const newPoints = typeof points === 'function' ? points(state.customCurvePoints) : points;
			dispatch({ type: 'SET_CUSTOM_CURVE_POINTS', payload: newPoints });
		},
		[state.customCurvePoints],
	);

	const setIndustryStandardCurve = useCallback((key: string) => {
		dispatch({ type: 'SET_INDUSTRY_CURVE', payload: key });
	}, []);

	const setOneExpectedUsefulLife = useCallback((value: number | null) => {
		dispatch({ type: 'SET_ONE_EXPECTED_LIFE', payload: value });
	}, []);

	const setCopyCurve = useCallback((key: string | null) => {
		dispatch({ type: 'SET_COPY_CURVE', payload: key });
	}, []);

	const setWeibullFormulation = useCallback((formulation: WeibullFormulation) => {
		dispatch({ type: 'SET_WEIBULL_FORMULATION', payload: formulation });
	}, []);

	const initialCurveConfig = useCallback(() => {
		dispatch({ type: 'SET_SELECTED_METHOD', payload: CurveMethod.UNDEFINED });
		dispatch({ type: 'SET_CUSTOM_CURVE_POINTS', payload: [] });
		dispatch({ type: 'SET_INDUSTRY_CURVE', payload: null });
		dispatch({ type: 'SET_INDUSTRY_CURVE_POINTS', payload: [] });
		dispatch({ type: 'SET_ONE_EXPECTED_LIFE', payload: null });
		dispatch({ type: 'SET_COPY_CURVE', payload: null });
		dispatch({ type: 'SET_COPIED_CURVE_POINTS', payload: [] });
		dispatch({ type: 'SET_WEIBULL_FORMULATION', payload: WeibullFormulation.TWO_PARAMETER });
	}, []);

	const setCurveMethodData = useCallback(() => {
		return {
			selectedCurveMethod: state.selectedCurveMethod,
			industryCurve: state.industryStandardCurve,
			customCurve: state.customCurvePoints,
			oneExpectedLife: state.oneExpectedUsefulLife,
			copiedCurve: state.copyCurve,
			weibullFormulation: state.weibullFormulation,
		};
	}, [state]);

	const clearLocalChanges = useCallback(() => undefined, []);

	const cancelChanges = useCallback(() => {
		initialCurveConfig();
	}, [initialCurveConfig]);

	return {
		selectedCurveMethod: state.selectedCurveMethod,
		setSelectedCurveMethod,
		customCurvePoints: state.customCurvePoints,
		setCustomCurvePoints,
		industryStandardCurve: state.industryStandardCurve,
		setIndustryStandardCurve,
		industryStandardCurvePoints: state.industryStandardCurvePoints,
		industryStandardCurveLoading: state.industryStandardCurveLoading,
		failureDefinitionCurvePoints: [],
		failureDefinitionCurveLoading: false,
		// Bulk targets span many groups, so "no curve fitted" stays unreportable here. The copy
		// source, though, is a single known group, so its fit availability is knowable.
		selectedFormulationUnavailable,
		// The copy source is a single known group, so when its fits recorded a reason the bulk
		// dialog can explain the fallback as fully as the per-group preview does. Gated on the method
		// like its two neighbours above: the reasons describe a copied weibull fit and mean nothing
		// once the dialog has moved to a custom or industry curve.
		selectedFormulationDeclineReason:
			state.selectedCurveMethod === CurveMethod.COPIED_CURVE
				? state.copiedCurveDeclines[state.weibullFormulation]
				: undefined,
		selectedFitDegenerate,
		// The bulk dialog never fetches a failure-definition inference, so it has no fitted
		// coefficients to show. The copied-curve fetch does not carry params either.
		selectedFormulationParams: undefined,
		noCurveFitted: false,
		copyCurve: state.copyCurve,
		setCopyCurve,
		copyCurveName: null,
		copiedCurvePoints,
		copiedCurveLoading: state.copiedCurveLoading,
		oneExpectedUsefulLife: state.oneExpectedUsefulLife,
		setOneExpectedUsefulLife,
		weibullFormulation: state.weibullFormulation,
		setWeibullFormulation,
		setCurveMethodData,
		initialCurveConfig,
		curveConfig: { inProcess: false, data: null },
		originalSavedConfig: null, // No saved config in bulk mode
		clearLocalChanges,
		cancelChanges,
	};
};
